import {
    WidgetBackgroundTheme,
    WidgetBorderTheme,
    WidgetButtonTheme,
    WidgetChatButtonTheme,
    WidgetChatButtonAnimationTheme,
    WidgetCtaTheme,
    WidgetFooterTheme,
    WidgetHeaderTheme,
    WidgetInputTheme,
    WidgetMarginsTheme,
    WidgetMenuItemTheme,
    WidgetMenuTheme,
    WidgetMessagesTheme,
    WidgetMessageTheme,
    WidgetPaddingsTheme,
    WidgetSizeTheme,
    WidgetTextTheme,
    WidgetTheme,
} from "@xapp/stentor-chat-widget";
import React from "react";

interface Props {
    readonly theme?: WidgetTheme;
}

export function buildStyleContent(theme?: WidgetTheme): string {
    return `
:root {
${buildVariables(
    withPrefix(
        "\t--xapp-",
        union(
            single("primary-color", theme?.primaryColor),
            withPrefix(
                "widget-",
                union(
                    getSize(theme?.size),
                    getMargins(theme?.margin),
                    getBorderStyle(theme?.border),
                    getZIndex(theme?.zIndex)
                )
            ),
            getChatButtonStyle(theme?.chatButton),
            withPrefix("header-", getHeaderStyle(theme?.header)),
            withPrefix("footer-", getFooterStyle(theme?.footer)),
            withPrefix("content-", union(getBackgroundStyle(theme?.content?.background))),
            withPrefix("messages-", getMessagesStyle(theme?.messages)),
            withPrefix("text-typing-status-", getTextStyle(theme?.textTypingStatus?.text)),
            withPrefix("send-", getButtonStyle(theme?.sendButton)),
            withPrefix("menu-", getButtonStyle(theme?.menuButton)),
            withPrefix("menu-", getMenuStyle(theme?.menu)),
            withPrefix("refresh-", getButtonStyle(theme?.refreshButton)),
            withPrefix("minimize-", getButtonStyle(theme?.minimizeButton)),
            withPrefix("cancel-", getButtonStyle(theme?.cancelButton)),
            withPrefix(
                "carousel-",
                union(
                    withPrefix("title-", getTextStyle(theme?.carousel?.title)),
                    withPrefix("subtitle-", getTextStyle(theme?.carousel?.subtitle)),
                    withPrefix("action-", getButtonStyle(theme?.carousel?.button))
                )
            ),
            withPrefix("input-", getInputStyle(theme?.input)),
            withPrefix("cta-", getCtaStyle(theme?.cta))
        )
    )
)}
}`;
}

export default function WidgetStylesheet(props: Props): JSX.Element {
    const { theme } = props;

    const stylesContent = buildStyleContent(theme);
    return <style>{stylesContent}</style>;
}

declare type StyleGenerator = Generator<[string, string], void, never>;

function buildVariables(gen: StyleGenerator) {
    let result = gen.next();
    let res = "";
    while (result.done === false) {
        const [key, value] = result.value;
        if (value !== undefined && value !== null && value !== "") {
            res += `${key}: ${value};\n\r`;
        }
        result = gen.next();
    }
    return res;
}

function* withPrefix(prefix: string, gen: StyleGenerator): StyleGenerator {
    let result = gen.next();
    while (!result.done) {
        yield [`${prefix}${result.value[0]}`, result.value[1]];
        result = gen.next();
    }
}

function* union(...children: StyleGenerator[]): StyleGenerator {
    for (const child of children) {
        let result = child.next();
        while (result.done === false) {
            yield result.value;
            result = child.next();
        }
    }
}

function* single(key: string, value: string): StyleGenerator {
    yield [key, value];
}

function* empty(): StyleGenerator {}

function getChatButtonStyle(style?: WidgetChatButtonTheme): StyleGenerator {
    if (!style) {
        return empty();
    }
    return withPrefix(
        "chat-button-",
        union(
            getMargins(style.margin), 
            getBackgroundStyle(style.background),
            withPrefix("image-", getSize(style.imageSize)),
            getAnimationStyle(style.animation)
        )
    );
}

function getMessagesStyle(style?: WidgetMessagesTheme): StyleGenerator {
    if (!style) {
        return empty();
    }
    return union(
        withPrefix("mine-", getMessageStyle(style.mine)),
        withPrefix("others-", getMessageStyle(style.others)),
        getPaddings(style?.padding),
        single("max-width", style?.maxWidth)
    );
}

function getMessageStyle(style?: WidgetMessageTheme): StyleGenerator {
    return union(single("bubble-color", style?.bubbleColor), getTextStyle(style?.text));
}

function* getSize(style?: WidgetSizeTheme): StyleGenerator {
    yield ["width", style?.width];
    yield ["height", style?.height];
}

function* getAnimationStyle(style?: WidgetChatButtonAnimationTheme): StyleGenerator {
    if (!style) {
        return;
    }
    
    const primaryColor = style.primaryColor || "0, 117, 170";
    const secondaryColor = style.secondaryColor || "110, 178, 209"; 
    const opacity = style.opacity || 0.75;
    
    // Generate CSS variables that can be used by any animation type
    yield ["animation-primary-color", `${primaryColor}`];
    yield ["animation-secondary-color", `${secondaryColor}`];
    yield ["animation-opacity", `${opacity}`];
    
    // Generate colors for box-shadow halo effects
    yield ["animation-primary-rgba", `rgba(${primaryColor}, ${opacity})`];
    yield ["animation-secondary-rgba", `rgba(${secondaryColor}, ${opacity})`];
    
    // Generate pulse-specific gradients (can be extended for other animations)
    const intenseBackground = `radial-gradient(40.83% 40.83% at 47.92% 52.5%, rgba(${primaryColor}, ${opacity}) 78.85%, rgba(${secondaryColor}, ${opacity}) 92.07%)`;
    const lessIntenseBackground = `radial-gradient(40.83% 40.83% at 47.92% 52.5%, rgba(${primaryColor}, ${opacity}) 20.46%, rgba(${secondaryColor}, ${opacity}) 92.07%)`;
    
    yield ["animation-pulse-intense", intenseBackground];
    yield ["animation-pulse-less-intense", lessIntenseBackground];
}

function* getZIndex(style?: string): StyleGenerator {
    if (style) {
        yield ["z-index", style];
    }
}

function* getMargins(style?: WidgetMarginsTheme): StyleGenerator {
    if (style) {
        yield [`margin-top`, style.top];
        yield [`margin-right`, style.right];
        yield [`margin-bottom`, style.bottom];
        yield [`margin-left`, style.left];
    }
}

function* getPaddings(style?: WidgetPaddingsTheme): StyleGenerator {
    if (style) {
        yield [`padding-top`, style.top];
        yield [`padding-right`, style.right];
        yield [`padding-bottom`, style.bottom];
        yield [`padding-left`, style.left];
    }
}

function* getTextStyle(style?: WidgetTextTheme): StyleGenerator {
    if (style) {
        yield ["text-color", style.color];
        yield ["font-size", style.fontSize];
        yield ["font-family", style.fontFamily];
        yield ["font-weight", style.fontWeight];
        yield ["font-style", style.fontStyle];
    }
}

function* getBackgroundStyle(style?: WidgetBackgroundTheme): StyleGenerator {
    yield ["background", style];
}

function* getButtonStyle(style?: WidgetButtonTheme): StyleGenerator {
    yield ["button-color", style?.color];
    yield ["button-width", style?.width];
    yield ["button-bg", style?.background];
    yield ["button-hover-bg", style?.hoverBackground];
}

function getMenuItemStyle(style?: WidgetMenuItemTheme): StyleGenerator {
    return union(
        getTextStyle(style?.text),
        withPrefix("subtitle-", getTextStyle(style?.subtitle)),
        getBorderStyle(style?.border),
        getBackgroundStyle(style?.background),
        single("height", style?.height)
    );
}

function getMenuStyle(style?: WidgetMenuTheme): StyleGenerator {
    return union(withPrefix("item-", getMenuItemStyle(style?.item)));
}

function* getBorderStyle(style?: WidgetBorderTheme): StyleGenerator {
    if (style) {
        yield ["border-color", style.color];
        yield ["border-width", style.width];
        yield ["border-radius", style.radius];
    }
}

function getHeaderStyle(style?: WidgetHeaderTheme): StyleGenerator {
    if (!style) {
        return empty();
    }
    return union(
        getBackgroundStyle(style.background),
        getTextStyle(style.text),
        withPrefix("subtitle-", getTextStyle(style.subtitle)),
        getBorderStyle(style.border),
        getPaddings(style.padding),
        single("avatar-background", style.avatarBackground)
    );
}

function getFooterStyle(style?: WidgetFooterTheme): StyleGenerator {
    if (!style) {
        return empty();
    }
    return union(
        getBackgroundStyle(style.background),
        getBorderStyle(style.border),
        withPrefix("branding-", getTextStyle(style.branding?.text))
    );
}

function getInputStyle(style?: WidgetInputTheme): StyleGenerator {
    if (!style) {
        return empty();
    }
    return union(
        getBackgroundStyle(style.background),
        getBorderStyle(style.border),
        getTextStyle(style.text),
        withPrefix("placeholder-", getTextStyle(style.placeholder))
    );
}

function getCtaStyle(style?: WidgetCtaTheme): StyleGenerator {
    if (!style) {
        return empty();
    }
    return union(getBackgroundStyle(style.background), getTextStyle(style.text));
}
